/**
 * Synthetic examples of the skeleton and editor routes (1.0b deliverables 7 and 9), set in "The
 * Drowned Lanterns". Every id in a request is an `EXAMPLE_IDS` value; the answers are shaped as
 * the real server gives them, with stand-in ids where the real value is another package's
 * (a built-in type's id is 1.0a's `builtinId`, which contracts cannot compute).
 */
import { docMetaFrom } from '../dto/documents.js';
import { formatMoveToken } from '../editor/move-token.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_NOW } from './examples.js';
/**
 * Stand-ins for the ids of the built-in types the examples name. Answers only: a request names a
 * type by its key, or a user type by its `EXAMPLE_IDS` value.
 */
export const EXAMPLE_TYPE_IDS = {
    quest: '7b3f0c52-1d4e-5a6b-8c7d-0e1f2a3b0001',
    lore: '7b3f0c52-1d4e-5a6b-8c7d-0e1f2a3b0002',
    location: '7b3f0c52-1d4e-5a6b-8c7d-0e1f2a3b0003',
    npc: '7b3f0c52-1d4e-5a6b-8c7d-0e1f2a3b0004',
    note: '7b3f0c52-1d4e-5a6b-8c7d-0e1f2a3b0005',
    ref_creature: '7b3f0c52-1d4e-5a6b-8c7d-0e1f2a3b0006',
};
const ids = {
    lamplighters: EXAMPLE_IDS.questMissingLamplighters.id,
    chapel: EXAMPLE_IDS.questSunkenChapel.id,
    bells: EXAMPLE_IDS.questBellsBeneathTheMarsh.id,
    history: EXAMPLE_IDS.loreHistoryOfVesk.id,
    quarter: EXAMPLE_IDS.locationLanternQuarter.id,
    ledger: EXAMPLE_IDS.noteTornLedger.id,
    notes: EXAMPLE_IDS.noteLamplighterNotes.id,
    arc: EXAMPLE_IDS.arcRisingWater.id,
    maelis: EXAMPLE_IDS.npcMaelisDorn.id,
};
function paragraph(id, text) {
    return { type: 'paragraph', attrs: { id }, content: [{ type: 'text', text }] };
}
/** One empty paragraph without an id, which the server's `normalizeIds` would mint. */
function emptyBody() {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
}
/** The body of Missing Lamplighters, with the paragraph that names Old Sef in plain text. */
export const EXAMPLE_BODY = {
    type: 'doc',
    content: [
        paragraph(EXAMPLE_IDS.blockMissingLamplightersOldSef.id, 'Old Sef swears the lamplighters walked into the tide the night the bells fell silent.'),
    ],
};
/** A canon document as it stands in a fresh database: never saved since it was written. */
function document(fields) {
    return {
        library: 'canon',
        titlePending: false,
        icon: null,
        coverAssetId: null,
        summary: null,
        aliases: [],
        bodySchemaVersion: 1,
        props: {},
        canonStatus: 'canon',
        aiContext: 'when_linked',
        autolink: true,
        isStub: false,
        mergedIntoId: null,
        bodyVersion: 1,
        metaVersion: 1,
        wordCount: null,
        playState: null,
        discovery: null,
        deletedAt: null,
        createdAt: EXAMPLE_NOW,
        updatedAt: EXAMPLE_NOW,
        ...fields,
    };
}
export const EXAMPLE_DOC_ID = ids.lamplighters;
/** Missing Lamplighters, as `docs.get` answers it. */
export const EXAMPLE_DOC = document({
    id: ids.lamplighters,
    fid: 'LanternQuest0001',
    typeId: EXAMPLE_TYPE_IDS.quest,
    typeKey: 'quest',
    title: 'Missing Lamplighters',
    body: EXAMPLE_BODY,
    playState: 'played',
    discovery: 'known',
    wordCount: 15,
});
/** The same document as `docs.meta` answers it: home under the arc The Rising Water. */
export const EXAMPLE_DOC_META = docMetaFrom(EXAMPLE_DOC, {
    capabilities: ['structural', 'playable', 'discoverable'],
    homeId: ids.arc,
});
/** The body `docs.create`'s example sends: one paragraph. */
export const EXAMPLE_CREATE_BODY = {
    type: 'doc',
    content: [
        paragraph(EXAMPLE_IDS.blockDrownedBellOpening.id, 'A bell rings under the water whenever the tide turns.'),
    ],
};
/** The quest `docs.create`'s example makes. */
export const EXAMPLE_CREATED_DOC = document({
    id: EXAMPLE_IDS.docCreatedByExample.id,
    fid: 'DrownedBell00001',
    typeId: EXAMPLE_TYPE_IDS.quest,
    typeKey: 'quest',
    title: 'The Drowned Bell',
    body: EXAMPLE_CREATE_BODY,
    canonStatus: 'draft',
});
/**
 * The body `docs.putBody`'s example saves into The Sunken Chapel: its own opening paragraph, then
 * a paragraph cut from Bells Beneath the Marsh, which the request's one move names.
 */
export const EXAMPLE_SAVE_BODY = {
    type: 'doc',
    content: [
        paragraph(EXAMPLE_IDS.blockSunkenChapelOpening.id, 'Brine seeps between the flagstones of the chapel floor.'),
        paragraph(EXAMPLE_IDS.blockBellsMoved.id, 'The marsh bells answer one another from beneath the reeds.'),
    ],
};
/**
 * The token of the example move (deliverable 16b): built by `formatMoveToken` from its source
 * page, Bells Beneath the Marsh, at body version 1, the version its `EXAMPLE_IDS` entry states.
 */
export const EXAMPLE_MOVE_TOKEN = formatMoveToken(ids.bells, 1);
/** The child page the `docs.moveBlocks` example creates, as its answer lists it. */
export const EXAMPLE_MOVED_PAGE_META = docMetaFrom(document({
    id: EXAMPLE_IDS.loreTheFloodCreated.id,
    fid: 'TheFloodPage0001',
    typeId: EXAMPLE_TYPE_IDS.lore,
    typeKey: 'lore',
    title: 'The Flood',
    body: emptyBody(),
    canonStatus: 'draft',
}), { capabilities: ['discoverable'], homeId: ids.history });
/** `docs.titles`' example answer: a live, a trashed, a merged and a missing id. */
export const EXAMPLE_TITLES = [
    {
        id: ids.lamplighters,
        fid: 'LanternQuest0001',
        title: 'Missing Lamplighters',
        titlePending: false,
        typeKey: 'quest',
        library: 'canon',
        isStub: false,
        state: 'live',
        mergedIntoId: null,
    },
    {
        id: ids.ledger,
        fid: 'TornLedgerNote01',
        title: 'A Torn Ledger',
        titlePending: false,
        typeKey: 'note',
        library: 'canon',
        isStub: false,
        state: 'trashed',
        mergedIntoId: null,
    },
    {
        id: ids.notes,
        fid: 'LamplighterNote1',
        title: 'Lamplighter Notes',
        titlePending: false,
        typeKey: 'note',
        library: 'canon',
        isStub: false,
        state: 'merged',
        mergedIntoId: ids.lamplighters,
    },
    {
        id: EXAMPLE_IDS.docMissing.id,
        fid: null,
        title: null,
        titlePending: null,
        typeKey: null,
        library: null,
        isStub: null,
        state: 'missing',
        mergedIntoId: null,
    },
];
/** `search.picker`'s example answer for "Mael". */
export const EXAMPLE_PICKER_RESULT = {
    canon: [
        {
            id: ids.maelis,
            fid: 'MaelisDornNpc001',
            title: 'Maelis Dorn',
            typeKey: 'npc',
            library: 'canon',
            isStub: false,
            matched: 'title',
            alias: null,
        },
    ],
    reference: [],
    exact: { canon: false, reference: false },
};
/** An example type summary (the mock's `listTypes` builds the full list). */
export const EXAMPLE_TYPE_SUMMARY = {
    id: EXAMPLE_TYPE_IDS.quest,
    key: 'quest',
    name: 'Quest',
    namePlural: 'Quests',
    icon: 'quest',
    color: 'quest',
    library: 'canon',
    capabilities: ['structural', 'playable', 'discoverable'],
    archived: false,
};
/**
 * Every document the docs examples name, in the state their examples need, for a mock document
 * store (`createDocumentStore({docs: EXAMPLE_DOCUMENTS})`): with them, the mock answers every
 * example of the docs routes as the real server answers it on `demo@p1a`.
 */
export const EXAMPLE_DOCUMENTS = [
    EXAMPLE_DOC,
    document({
        id: ids.chapel,
        fid: 'SunkenChapel0001',
        typeId: EXAMPLE_TYPE_IDS.quest,
        typeKey: 'quest',
        title: 'The Sunken Chapel',
        body: {
            type: 'doc',
            content: [
                paragraph(EXAMPLE_IDS.blockSunkenChapelOpening.id, 'Brine seeps between the flagstones of the chapel floor.'),
            ],
        },
    }),
    document({
        id: ids.bells,
        fid: 'BellsBeneath0001',
        typeId: EXAMPLE_TYPE_IDS.quest,
        typeKey: 'quest',
        title: 'Bells Beneath the Marsh',
        body: {
            type: 'doc',
            content: [
                paragraph(EXAMPLE_IDS.blockBellsMoved.id, 'The marsh bells answer one another from beneath the reeds.'),
            ],
        },
    }),
    document({
        id: ids.history,
        fid: 'HistoryOfVesk001',
        typeId: EXAMPLE_TYPE_IDS.lore,
        typeKey: 'lore',
        title: 'A History of Vesk',
        body: {
            type: 'doc',
            content: [
                {
                    type: 'heading',
                    attrs: { id: EXAMPLE_IDS.blockHistoryFloodHeading.id, level: 2 },
                    content: [{ type: 'text', text: 'The Flood' }],
                },
                paragraph(EXAMPLE_IDS.blockHistoryFloodText.id, 'In the long winter before the Age of Lanterns the sea came over the delta.'),
                paragraph(EXAMPLE_IDS.blockHistoryLanternQuarter.id, 'The first lamps of the Lantern Quarter were lit on the old sea wall.'),
            ],
        },
    }),
    document({
        id: ids.quarter,
        fid: 'LanternQuarter01',
        typeId: EXAMPLE_TYPE_IDS.location,
        typeKey: 'location',
        title: 'the Lantern Quarter',
        body: emptyBody(),
    }),
    document({
        id: ids.ledger,
        fid: 'TornLedgerNote01',
        typeId: EXAMPLE_TYPE_IDS.note,
        typeKey: 'note',
        title: 'A Torn Ledger',
        body: emptyBody(),
        deletedAt: EXAMPLE_NOW,
    }),
    document({
        id: ids.notes,
        fid: 'LamplighterNote1',
        typeId: EXAMPLE_TYPE_IDS.note,
        typeKey: 'note',
        title: 'Lamplighter Notes',
        body: emptyBody(),
        mergedIntoId: ids.lamplighters,
        deletedAt: EXAMPLE_NOW,
    }),
];
/** A canon document as a fresh database holds it, for the examples of other domains. */
export const exampleDocument = document;
//# sourceMappingURL=examples-docs.js.map